<template>
    <div>
        <b-container>
            <b-row>
                <b-col>
                    <h4>{{ $t('traffic_deviations.canceled_departures.passing_times') }}</h4>
                </b-col>
                <b-col>
                    <CanceledDeparturesButtons></CanceledDeparturesButtons>
                </b-col>
            </b-row>
            <b-row>
                <b-col>{{ selectedTripData.line }}</b-col>
                <b-col>{{ selectedTripData.destination }}</b-col>
                <b-col v-if="$UiConfig('/ui/view/dispatching/canceleddepartures/showTripId', false)">{{ selectedTripData.tripId }}</b-col>
                <b-col>{{ selectedTripData.time }}</b-col>
            </b-row>
            <b-row><b-col>
                <b-form-checkbox-group
                id="cb-canceled-passings"
                name="cb-canceled-passings"
                v-model="passingsShownAsSelected">
                <b-table
                    :fields="passingtimesListFields"
                    :items="passingtimesListItems">
                    <template #head(cancelingStatus)="data">
                        <input type="checkbox" :id="'cb-'+ data.label.toLowerCase() +'-all'" v-model="selectAllPassings" />
                    </template>
                    <template v-slot:cell(cancelingStatus)="data">
                        <b-form-checkbox :value="getPassingKey(data.item)" @click.native="handleCheckboxNativeClick"></b-form-checkbox>
                    </template>
                </b-table>
                </b-form-checkbox-group>
            </b-col></b-row>
        </b-container>
    </div>
</template>

<script>
import moment from 'moment';
import CanceledDeparturesButtons from '@/components/Dispatching/TrafficDeviations/CanceledDeparturesButtons.vue';
import { getPassingKey, isPassingCanceled } from '@/store/dispatching/trafficDeviations.js';

export default {
    name: 'PassingtimesTable',
    components: { CanceledDeparturesButtons },
    data() {
        return {
            passingsShownAsSelected: [],
            selectAllPassings: false,
            latestSelectedPassing: '',
            latestDeselectedPassing: ''
        };
    },
    computed: {
        passingtimesListFields() {
            return [
                { key: 'cancelingStatus', label: this.$t('traffic_deviations.canceled_departures.canceled'), sortable: false },
                { key: 'stopOrder', label: '#', sortable: false },
                { key: 'stopId', label: '', sortable: false },
                { key: 'stopName', label: this.$t('traffic_deviations.canceled_departures.stop'), sortable: false },
                { key: 'passingTime', label: this.$t('traffic_deviations.canceled_departures.time'), sortable: false, formatter: (value) => {
                    let time = moment.parseZone(value);
                    if (time.isValid()) {
                        return time.format('HH:mm');
                    }
                    return value;
                }}
            ];
        },
        passingtimesListItems() {
            if (this.selectedTrip && Array.isArray(this.selectedTrip.passings)) {
                return this.selectedTrip.passings;
            }
            return [];
        },
        selectedTrip() {
            let tripsList;
            if (this.$UiConfig('/ui/view/dispatching/canceleddepartures/cancelByCourse', false)) {
                let selectedCourse = this.$store.state.trafficDeviations.selectedCourseWithVehicles.courseNumber;
                tripsList = this.$store.state.trafficDeviations.tripsByCourse[selectedCourse];
            } else {
                let selectedBlockSequence = this.$store.state.trafficDeviations.selectedBlockSequence.blockSequenceId;
                tripsList = this.$store.state.trafficDeviations.tripsByBlockSequence[selectedBlockSequence];
            }
            if (Array.isArray(tripsList) && tripsList.length > 0) {
                let matchingTrips = tripsList.filter(trip => trip.tripId == this.$store.state.trafficDeviations.selectedTripId);
                if (Array.isArray(matchingTrips) && matchingTrips.length > 0) {
                    return matchingTrips[0];
                }
            }
            return null;
        },
        selectedTripData() {
            if (!this.selectedTrip)
                return { line: '', destination: '', tripId: '', time: '' };

            return {
                line: this.selectedTrip.lineNumber,
                destination: this.selectedTrip.destination,
                tripId: this.selectedTrip.tripId,
                time: this.getTripTime(this.selectedTrip)
            }
        },
        tripCancellationTimestamp() {
            return this.$store.state.trafficDeviations.tripCancellationTimestamp;
        }
    },
    watch: {
        passingtimesListItems(passingtimes) {
            this.updateVisibleSelections(passingtimes);
        },
        passingsShownAsSelected(selected) {
            if (!Array.isArray(selected) || !this.selectedTrip)
                return;

            for (const passing of this.selectedTrip.passings) {
                let passingSelected = false;
                for (const passingKey of selected) {
                    if (passingKey == getPassingKey(passing)) {
                        passingSelected = true;
                        this.$store.commit('trafficDeviations/deleteCancellationToRemove', passingKey);
                        if (!isPassingCanceled(passing)) {
                            this.$store.commit('trafficDeviations/putCancellationToAdd', { key: passingKey, passing: passing });
                        }
                        break;
                    }
                }
                if (!passingSelected) {
                    this.$store.commit('trafficDeviations/deleteCancellationToAdd', getPassingKey(passing));
                    if (isPassingCanceled(passing)) {
                        this.$store.commit('trafficDeviations/putCancellationToRemove',
                            { key: getPassingKey(passing), passing: passing });
                    }
                }
            }
        },
        selectAllPassings(selectAll) {
            this.latestSelectedPassing = '';
            this.latestDeselectedPassing = '';

            if (!selectAll) {
                this.passingsShownAsSelected = [];
                return;
            }
            let newSelection = [];
            for (const passing of this.passingtimesListItems) {
                newSelection.push(getPassingKey(passing));
            }
            this.passingsShownAsSelected = newSelection;
        },
        tripCancellationTimestamp(newTime, oldTime) {
            if (newTime.isAfter(oldTime)) {
                this.latestSelectedPassing = '';
                this.latestDeselectedPassing = '';

                this.updateVisibleSelections(this.passingtimesListItems);
            }
        },
        selectedTrip(newTrip, oldTrip) {
            if (!newTrip || !oldTrip || newTrip.tripId != oldTrip.tripId) {
                this.latestSelectedPassing = '';
                this.latestDeselectedPassing = '';
            }
        }
    },
    methods: {
        getPassingKey(passing) {
            return getPassingKey(passing);
        },
        getTripTime(trip) {
            let result = '';
            if (Array.isArray(trip.passings) && trip.passings.length > 0) {
                let start = moment.parseZone(trip.passings[0].departureTime);
                if (start.isValid()) {
                    result = start.format('HH:mm');

                    let lastPassing = trip.passings[trip.passings.length - 1];
                    let end = moment.parseZone(lastPassing.passingTime);
                    if (end.isValid()) {
                        result += '\xa0- '+ end.format('HH:mm');
                    }
                }
            }
            if (result.length > 0)
                return result;

            let time = moment.parseZone(trip.departureTime);
            if (time.isValid()) {
                return time.format('HH:mm');
            }
            return trip.departureTime;
        },
        updateVisibleSelections(passingtimes) {
            let selected = [];
            if (Array.isArray(passingtimes)) {
                for (const passing of passingtimes) {
                    if ((isPassingCanceled(passing) && !this.$store.state.trafficDeviations.cancellationsToRemove[getPassingKey(passing)]) ||
                            this.$store.state.trafficDeviations.cancellationsToAdd[getPassingKey(passing)]) {
                        selected.push(getPassingKey(passing));
                    }
                }
            }
            this.passingsShownAsSelected = selected;
        },
        handleCheckboxNativeClick(e) {
            if (e.target.tagName === 'LABEL') {
                let forAttribute = e.target.getAttribute('for');
                let inputElement = document.getElementById(forAttribute);
                let passingKey = inputElement.value;
                let stopOrder = passingKey.split('##')[0];
                let shiftClick = e.getModifierState('Shift');

                if (!shiftClick) {
                    if (inputElement.checked) {
                        this.latestSelectedPassing = '';
                        this.latestDeselectedPassing = passingKey;
                    } else {
                        this.latestSelectedPassing = passingKey;
                        this.latestDeselectedPassing = '';
                    }
                    return;
                } else {
                    if (this.latestSelectedPassing) {
                        let previousOrder = this.latestSelectedPassing.split('##')[0];
                        if (parseInt(stopOrder) > parseInt(previousOrder)) {
                            this.selectRange(previousOrder, stopOrder);
                        } else {
                            this.selectRange(stopOrder, previousOrder);
                        }
                    }
                    if (this.latestDeselectedPassing) {
                        let previousOrder = this.latestDeselectedPassing.split('##')[0];
                        if (parseInt(stopOrder) > parseInt(previousOrder)) {
                            this.deselectRange(previousOrder, stopOrder);
                        } else {
                            this.deselectRange(stopOrder, previousOrder);
                        }
                    }
                }
            }
        },
        selectRange(first, last) {
            let selected = [...this.passingsShownAsSelected];
            let selectionRange = this.passingtimesListItems.slice(first - 1, last);

            for (const passing of selectionRange) {
                if (selected.indexOf(getPassingKey(passing)) < 0) {
                    selected.push(getPassingKey(passing));
                }
            }
            this.passingsShownAsSelected = selected;
        },
        deselectRange(first, last) {
            let selected = [];
            let deselectionRange = this.passingtimesListItems.slice(first - 1, last);

            let passingKeysToRemove = [];
            for (const passing of deselectionRange) {
                passingKeysToRemove.push(getPassingKey(passing));
            }

            for (const passingKey of this.passingsShownAsSelected) {
                if (passingKeysToRemove.indexOf(passingKey) < 0) {
                    selected.push(passingKey);
                }
            }
            this.passingsShownAsSelected = selected;
        }
    }
}
</script>